import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { toast } from "react-toastify";

import axiosInstance from "src/common/axiosInstance";
import WorkflowHeader from "../WorkflowHeader/WorkflowHeader";
import WorkflowPath from "./WorkflowPath";

const emptyWorkflow = {
    current_stage: null,
    current_node: null,
    completed: false,
    linear_path: [],
    branches: [],
};

export default function WorkflowActions({ leadId, onWorkflowChanged, workflow = emptyWorkflow, onRefresh, refreshing = false }) {
    const [working, setWorking] = useState(false);
    const [expandedBranches, setExpandedBranches] = useState({});

    async function completeNode(nodeId, nextNodeId = null) {
        try {
            setWorking(true);
            await axiosInstance.post(`/lead-workflow/${leadId}/complete-node`, {
                node_id: nodeId,
                next_node_id: nextNodeId,
            });
            setExpandedBranches({});
            await onWorkflowChanged?.();
        } catch (error) {
            toast.error(error?.response?.data?.message || "Unable to complete this activity.");
        } finally {
            setWorking(false);
        }
    }

    function toggleBranch(id, siblingIds = [], forceValue = null) {
        setExpandedBranches((previous) => {
            const next = { ...previous };
            siblingIds.forEach((siblingId) => {
                if (siblingId !== id) next[siblingId] = false;
            });
            next[id] = forceValue !== null ? forceValue : !previous[id];
            return next;
        });
    }

    return (
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <WorkflowHeader
                workflow={workflow}
                leadId={leadId}
                onRefresh={onRefresh}
                refreshing={refreshing}
                onWorkflowChanged={onWorkflowChanged}
            />

            <div className="border-t border-slate-100 p-4 sm:p-5">
                {workflow.completed && (
                    <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700"><CheckCircle2 className="h-5 w-5" /></span>
                        <div>
                            <h3 className="text-sm font-semibold text-emerald-900">Journey completed</h3>
                            <p className="text-xs text-emerald-700">All required activities in this pipeline are complete.</p>
                        </div>
                    </div>
                )}

                {!workflow.completed && (
                    <WorkflowPath
                        workflow={workflow}
                        working={working}
                        completeNode={completeNode}
                        toggleBranch={toggleBranch}
                        expandedBranches={expandedBranches}
                        currentNodeId={workflow.current_node?.id}
                    />
                )}
            </div>
        </section>
    );
}
